/**
 * Pure helper functions used by ProductsPage.
 *
 * Extracted from ProductsPage.js so the mega-component can
 * shrink and these small utilities can be unit-tested in isolation.
 * All functions are pure — they take arguments and return values, no
 * React state, no side effects.
 */

/**
 * Sort a list of {label, value, sqft} size options in the order HEDA's
 * sales team prefers on the size-picker: widest first (4' vs 3'),
 * longest first within each width (10' → 6'). Mutates and returns the
 * input array (matches the original in-place behaviour).
 */
export function sortPlywoodSizes(sizes) {
  return sizes.sort((a, b) => {
    const [aLength, aWidth] = a.value.toLowerCase().split('x').map(parseFloat);
    const [bLength, bWidth] = b.value.toLowerCase().split('x').map(parseFloat);
    // First sort by width (descending: 4, 3)
    if (bWidth !== aWidth) return bWidth - aWidth;
    // Then sort by length (descending: 10, 9, 8, 7, 6)
    return bLength - aLength;
  });
}

/**
 * Convert a "LxW" size string to square feet, honouring the incoming
 * unit. Falls back to 32 (8x4 sheet area) if the string is unparseable.
 *
 * Note: this is the ProductsPage-local flavour and is intentionally
 * distinct from `lib/sizeToSqftStandalone` — that one bakes in
 * per-category defaults (Doors treated as inches etc.) whereas this
 * one takes the unit as an explicit argument.
 */
export function convertSizeToSqFt(sizeStr, sizeUnit = 'feet') {
  if (!sizeStr || !sizeStr.includes('x')) return 32; // default 8x4
  const parts = sizeStr.toLowerCase().replace(/['"]/g, '').split('x');
  const dim1 = parseFloat(parts[0]) || 0;
  const dim2 = parseFloat(parts[1]) || 0;

  switch (sizeUnit.toLowerCase()) {
    case 'mm':
      // Convert mm to feet: 1 foot = 304.8mm
      return (dim1 / 304.8) * (dim2 / 304.8);
    case 'inches':
      // Convert inches to feet: 1 foot = 12 inches
      return (dim1 / 12) * (dim2 / 12);
    case 'meters':
      // Convert meters to feet: 1 meter = 3.28084 feet
      return (dim1 * 3.28084) * (dim2 * 3.28084);
    case 'feet':
    default:
      return dim1 * dim2;
  }
}

/**
 * Attach the unit suffix / quote-mark to a size string for on-screen
 * display. Empty input → empty output.
 */
export function formatSizeWithUnit(sizeStr, sizeUnit = 'feet') {
  if (!sizeStr) return '';
  switch (sizeUnit.toLowerCase()) {
    case 'mm':    return `${sizeStr} mm`;
    case 'inches': return `${sizeStr}"`;
    case 'meters': return `${sizeStr} m`;
    case 'feet':
    default:      return sizeStr;
  }
}

/**
 * List every rate option (box, dozen, piece, kg, running feet) present
 * on a Hardware variant, in a canonical order — for the rate-type
 * button-picker inside the product card.
 */
export function getHardwareRateOptions(variant) {
  if (!variant) return [];
  const options = [];
  if (variant.rate_box) options.push({ type: 'per box', rate: variant.rate_box, label: `₹${variant.rate_box}/box` });
  if (variant.rate_dozen) options.push({ type: 'per dozen', rate: variant.rate_dozen, label: `₹${variant.rate_dozen}/dozen` });
  if (variant.rate_piece) options.push({ type: 'per piece', rate: variant.rate_piece, label: `₹${variant.rate_piece}/piece` });
  if (variant.rate_kg) options.push({ type: 'per kg', rate: variant.rate_kg, label: `₹${variant.rate_kg}/kg` });
  if (variant.rate_running_feet) options.push({ type: 'per running feet', rate: variant.rate_running_feet, label: `₹${variant.rate_running_feet}/rft` });
  return options;
}

/**
 * Read the rate value off a Hardware variant for a specific rate type
 * (used when a variant has multiple rates and staff has picked one).
 */
export function getHardwareRate(variant, rateType) {
  if (!variant || !rateType) return 0;
  switch (rateType) {
    case 'per box':          return variant.rate_box || 0;
    case 'per dozen':        return variant.rate_dozen || 0;
    case 'per piece':        return variant.rate_piece || 0;
    case 'per kg':           return variant.rate_kg || 0;
    case 'per running feet': return variant.rate_running_feet || 0;
    default:                 return 0;
  }
}

/**
 * Cart-column unit label for a Hardware rate type. Matches HEDA's
 * standard abbreviations (BOX, DZN, NOS, KG, RFT).
 */
export function getHardwareUnit(rateType) {
  switch (rateType) {
    case 'per box':          return 'BOX';
    case 'per dozen':        return 'DZN';
    case 'per piece':        return 'NOS';
    case 'per kg':           return 'KG';
    case 'per running feet': return 'RFT';
    default:                 return 'NOS';
  }
}
